/**
 * Single source of truth for all colour values.
 * No MUI imports — these are plain values usable anywhere
 * (Recharts, canvas, CSS-in-JS, emails, etc.)
 *
 * THREE LAYERS:
 *   1. colorTokens   — raw hex/rgba, named exactly as Figma "Primitives" collection
 *   2. textTokens    — semantic text roles (Figma "Text" collection)
 *   3. uiTokens      — semantic UI roles  (Figma "Tokens" collection)
 *   4. LegacyPrimary / LegacySecondary — v1 values kept during migration (see declarations below)
 *
 * Transparency naming convention: <source>At<opacityPercent>
 *   e.g. transparencyDarkAt23 = rgba(0, 0, 0, 0.23)
 *
 *  FIGMA reference:
 *  https://www.figma.com/design/CqO24eewdxuBg3MkLbj2QX/BHB-components?node-id=6112-51415&t=cqV7qec26xmWuSV7-0
 */

// COLOR TOKENS  (Figma: "Primitives" collection)
export const colorTokens = {
  // Primary scale
  primary: "#134063",
  primary900: "#205C82",
  primary800: "#205C82", // same hex as 900 per Figma export
  primary100: "#BAD9E4",
  primary50: "#E3F0F3",

  // Secondary scale
  secondary: "#F6724B",
  secondary50: "#F9E9E8",
  secondaryDark: "#B53C19",

  // Brand secondary
  brandSecondary: "#FFEEDF",

  // Complementary (teal)
  complementaryDark: "#13736D",
  complementary50: "#E0F1F2",
  complementaryLightest: "#F3F9FA",

  // Accents
  brightBlue: "#BBF6EC",
  brightRed: "#F84B6E",

  // Gradients
  gradientTop: "#F3F5FE",
  gradientBottom: "#E3F0F3",

  // Neutrals
  lightest: "#FFFFFF",
  darkest: "#000000",
  dark38: "#9E9E9E",

  // Text primitives (Figma: "Text" collection)
  textPrimary: "#1E1E1E",
  textSecondary: "#757575",
  textTertiary: "#8E8E8E",

  // Feedback
  error: "#D32F2F",

  // Transparencies — <source>At<opacityPercent>
  transparencyPrimary900At12: "rgba(32, 92, 130, 0.12)",
  transparencyDarkAt54: "rgba(0, 0, 0, 0.54)",
  transparencyDarkAt38: "rgba(0, 0, 0, 0.38)",
  transparencyDarkAt23: "rgba(0, 0, 0, 0.23)",
  transparencyDarkAt12: "rgba(0, 0, 0, 0.12)",
  transparencyDarkAt5: "rgba(0, 0, 0, 0.05)",
  transparencyLightAt54: "rgba(255, 255, 255, 0.54)",
  transparencySecondaryAt10: "rgba(246, 114, 75, 0.10)"
} as const;

export type ColorTokens = typeof colorTokens;

// TEXT TOKENS  (Figma: "Text" collection)
export const textTokens = {
  dark: {
    primary: colorTokens.textPrimary,
    secondary: colorTokens.textSecondary,
    tertiary: colorTokens.textTertiary,
    disabled: colorTokens.dark38,
    error: colorTokens.error,
    link: colorTokens.primary
  },
  light: {
    primary: colorTokens.lightest,
    secondary: colorTokens.lightest,
    tertiary: colorTokens.lightest,
    disabled: colorTokens.transparencyLightAt54,
    error: colorTokens.transparencyLightAt54,
    focus: colorTokens.lightest
  }
} as const;

export type TextTokens = typeof textTokens;

// UI TOKENS  (Figma: "Tokens" collection)
export const uiTokens = {
  // --- Fills ---
  fill: colorTokens.lightest,
  alertBlueFill: colorTokens.primary50,
  alertRedFill: colorTokens.secondary50,
  apptSummaryBooked: colorTokens.lightest,
  apptSummaryNotBooked: colorTokens.secondary50,
  chipSelectedFill: colorTokens.primary50,
  menuHover: colorTokens.primary50,
  practiceMessageFill: colorTokens.brightBlue,
  regularPatientsFill: colorTokens.primary50,
  gradientTop: colorTokens.gradientTop,
  gradientBottom: colorTokens.gradientBottom,

  // --- Borders ---
  border: colorTokens.transparencyDarkAt23,
  buttonSecondaryBorder: colorTokens.primary,
  emptyBorder: colorTokens.primary100,
  fabBorder: colorTokens.transparencyPrimary900At12,
  groupBorder: colorTokens.primary,
  inputBorder: colorTokens.transparencyDarkAt23,
  inputBorderDisabled: colorTokens.transparencyDarkAt23,
  inputBorderFocus: colorTokens.primary,
  stepperTodoBorder: colorTokens.transparencyDarkAt23,
  topBarBorder: colorTokens.primary50,

  // --- Icons ---
  alertBlueIcon: colorTokens.primary,
  alertRedIcon: colorTokens.secondaryDark,
  clearIcon: colorTokens.primary,
  dashboardIcon: colorTokens.primary,
  disabledIcon: colorTokens.dark38,
  dropdownIcon: colorTokens.textSecondary,
  iconLight: colorTokens.lightest,
  iconPrimary: colorTokens.primary,
  inputDropdownIcon: colorTokens.transparencyDarkAt54,
  messageIcon: colorTokens.secondary,
  practiceMsgIcon: colorTokens.primary,
  profileIcon: colorTokens.primary,
  profileIconFillInverse: colorTokens.lightest,
  warningIcon: colorTokens.textPrimary,

  // --- Interactive ---
  buttonPrimary: colorTokens.primary,
  calendarSelectedDay: colorTokens.primary,
  calendarToday: colorTokens.primary,
  checkboxSelectedFill: colorTokens.primary,
  chipSelected: colorTokens.primary,
  clearBorder: colorTokens.primary,
  favourite: colorTokens.brightRed,
  menuIconSelected: colorTokens.primary,
  profileAvatarFill: colorTokens.primary,
  stepperCompleted: colorTokens.secondary,
  stepperMobileComplete: colorTokens.primary,
  stepperMobileTodo: colorTokens.primary100,
  stepperTodoFill: colorTokens.primary,
  toggleBackgroundOn: colorTokens.primary,
  toggleBackgroundOff: colorTokens.dark38,
  toggleBackgroundDisabled: colorTokens.transparencyDarkAt23,
  toggleButton: colorTokens.lightest,
  toggleButtonDisabled: colorTokens.transparencyLightAt54,

  // --- Misc ---
  accordionHeaderFill: colorTokens.transparencyPrimary900At12,
  accordionBadgeFill: colorTokens.transparencyPrimary900At12,
  apptSummaryBookedText: colorTokens.complementaryDark,
  apptSummaryNotBookedText: colorTokens.secondaryDark,
  coinPatientFamilyFill: colorTokens.transparencyPrimary900At12,
  coinProviderFill: colorTokens.primary100,
  deleteButton: colorTokens.error,
  earliestApptBorder: colorTokens.primary50,
  emergency: colorTokens.secondaryDark,
  logo: colorTokens.primary,
  logoInverse: colorTokens.lightest,
  sectionDivider: colorTokens.transparencyDarkAt12,
  stepperDivider: colorTokens.textTertiary,
  textareaBackground: colorTokens.transparencyDarkAt5,
  topBarFill: colorTokens.primary,
  badgeCornerRadius: 4
} as const;

export type UITokens = typeof uiTokens;

// LEGACY  — @deprecated, kept for safe migration
// Remove once all theme.palette.primary[x] call-sites are updated.
/** @deprecated Use colorTokens.primary / primary900 / etc. */
export const LegacyPrimary = {
  50: "#E3F0F3",
  100: "#BAD9E4",
  200: "#93C1D4",
  300: "#6FA9C2",
  400: "#5698B8",
  500: "#4088AF",
  600: "#357CA3",
  700: "#296C93",
  800: "#205C82",
  900: "#134063"
} as const;

/** @deprecated Use colorTokens.secondary / secondaryDark / etc. */
export const LegacySecondary = {
  50: "#F9E9E8",
  100: "#FBCCBE",
  200: "#F9AC95",
  300: "#F78C6B",
  400: "#F6724B",
  500: "#F55C2E",
  600: "#EA562A",
  700: "#DC4F25",
  800: "#CE4922",
  900: "#B53C19"
} as const;
